import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ricerca } from './anagrafiche.js';
import { chiama } from './cliente.js';

/** Una riga dell'elenco ordini. */
export interface RigaOrdine {
  id: number;
  orderNumber: string;
  protocolNumber: number;
  sectional: string | null;
  orderDate: string;
  customerId: number;
  customer: string;
  destination: string | null;
  destinationCountry: string | null;
  stateId: number;
  /** Nella lingua di chi guarda. */
  state: string;
  /** Il nome a database, sempre in inglese. */
  stateOriginal: string;
  stateColor: string | null;
  stateOrder: number;
  /** Il numero che si vede: nella Inc il 5 è il 3, e quelli a riposo non ne hanno. */
  stateNumber: number | null;
  vessel: string | null;
  container: string | null;
  arrival: string | null;
  arrivalIsEstimate: boolean;
  deliveryDateRequested: string | null;
  late: boolean;
  currency: string | null;
  total: string | null;
  paymentStatus: 'unpaid' | 'partial' | 'paid';
  paidAmount: string | null;
  invoiceNumber: string | null;
  carrier: string | null;
  carrierSent: boolean;
  archived: boolean;
}

export interface PassoRotta {
  id: number;
  name: string;
  nameOriginal: string;
  sortOrder: number;
  /** Il numero che si vede: nella Inc il 5 è il 3, e quelli a riposo non ne hanno. */
  number: number | null;
  colorHex: string | null;
  reachedAt: string | null;
  current: boolean;
  done: boolean;
  skipped: boolean;
}

export interface RigaOrdineDettaglio {
  id: number;
  articleId: number | null;
  sku: string | null;
  bcNumber: string | null;
  description: string | null;
  crossReference: string | null;
  quantity: number;
  priceExw: string;
  costDdp: string;
  discountPercentage1: string | null;
  discountPercentage2: string | null;
  discountPercentage3: string | null;
  pricelistId: number | null;
  pricelist: string | null;
  total: string;
}

export interface Nave {
  id: number;
  vessel: string | null;
  estimatedDepartureTime: string | null;
  estimatedArrivalTime: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  customsDate: string | null;
  customsNumber: string | null;
  customsProformaDate: string | null;
  customsProformaNumber: string | null;
  containers: {
    id: number;
    container: string;
    estimatedDeliveryTime: string | null;
    deliveryTime: string | null;
  }[];
}

export interface Ordine {
  id: number;
  orderNumber: string;
  protocolNumber: number;
  orderDate: string;
  sectional: string | null;
  sectionalCode: string | null;
  customerId: number;
  customer: string;
  customerBcCode: string | null;
  /** Vuota se il cliente non ha indirizzi: in BC vale il suo. */
  addressId: number | null;
  destination: string | null;
  destinationAddress: string | null;
  destinationCity: string | null;
  destinationPostalCode: string | null;
  destinationProvinceState: string | null;
  destinationCountry: string | null;
  stateId: number;
  /** Nella lingua di chi guarda. */
  state: string;
  /** Il nome a database, sempre in inglese. */
  stateOriginal: string;
  stateColor: string | null;
  stateOrder: number;

  invoiceNumberSpa: string | null;
  invoiceDateSpa: string | null;
  invoiceNumberInc: string | null;
  invoiceDateInc: string | null;

  deliveryDateRequested: string | null;
  goodsReadyShippingDate: string | null;
  goodsShippedDate: string | null;
  estimatedDepartureDate: string | null;
  estimatedArrivalDate: string | null;
  estimatedDeliveryDate: string | null;
  arrivalDate: string | null;
  deliveryDate: string | null;

  /** Il PDF della fattura caricato a mano: il nome, o niente. */
  invoiceFileNameSpa: string | null;
  invoiceFileNameInc: string | null;

  carrierId: number | null;
  carrier: string | null;
  /** `cl-automation`, `interglobo-automation`, oppure niente. */
  carrierAutomation: string | null;
  shipCarrier: string | null;
  containerShipmentNumber: string | null;
  /** Il vettore ha scritto `DEFAULT`: non indica la nave (o il container). */
  shipCarrierNotGiven?: boolean;
  containerNotGiven?: boolean;
  seal: string | null;
  carrierSent: boolean;
  carrierAcknowledged: boolean;
  shipmentNotes: string | null;

  paymentTypeId: number;
  paymentType: string | null;
  bankId: number;
  bank: string | null;
  estimatedPaymentDate: string | null;
  paymentDate: string | null;
  paymentStatus: 'unpaid' | 'partial' | 'paid';
  paidAmount: string | null;
  penaltiesAmount: string | null;

  currency: string | null;
  taxable: string | null;
  tax: string | null;
  total: string | null;

  notes: string | null;
  archived: boolean;

  /** `SPA` o `INC`: le regole cambiano fra le due. */
  companyCode: string;
  /** I campi della testata che in questo stato si possono correggere. */
  editable: string[];
  /** Le righe si aggiungono e si cambiano agli stati 1-2… */
  linesEditable: boolean;
  /** …e si tolgono fino al 3. */
  linesDeletable: boolean;
  /** Solo dopo un passaggio allo stato 3: com'è andato l'invio automatico. */
  carrierSend?: { sent: boolean; message: string };
  /** Solo dopo un passaggio di stato che scrive su BC, se è partito qualcosa. */
  bcSend?: EsitoInvioBc;

  lines: RigaOrdineDettaglio[];
  vessels: Nave[];
  invoices: {
    id: number;
    invoiceNumber: string;
    invoiceDate: string;
    fileName: string;
    issuer: string | null;
  }[];
  attachments: { id: number; title: string; fileName: string | null; notes: string | null }[];
  history: {
    id: number;
    state: string | null;
    /** Il nome com'è scritto a database, in inglese: serve a chi legge in inglese. */
    stateOriginal: string | null;
    stateChanged: boolean;
    /** `created` sulla riga che racconta la nascita dell'ordine. */
    kind: 'created' | 'modified';
    /** Il registro legacy arriva già smontato: mai HTML da queste parti. */
    changes: { field: string; from?: string; to?: string; value?: string }[];
    /** Una frase intera che non si scompone in campi. */
    note: string | null;
    /** Vero sulle righe scritte correggendo lo stato a mano. */
    correction: boolean;
    operator: string | null;
    createdAt: string;
  }[];
  tracking: {
    vessel: string | null;
    containerNumber: string | null;
    status: string | null;
    estimatedArrivalTime: string | null;
    acquiredAt: string;
  } | null;
  route: PassoRotta[];
}

interface Pagina {
  rows: RigaOrdine[];
  total: number;
  page: number;
  size: number;
  pages: number;
  /** Solo quando nella casella c'è un elenco di PO: quanti erano e quali mancano. */
  poList?: { searched: number; notFound: string[] };
}

export const RICERCA_ORDINI: Ricerca = {
  page: 1,
  size: 50,
  // l'ordine più recente in cima: è quello di cui si parla al telefono
  sort: 'orderDate',
  dir: 'desc',
  q: '',
  archived: false,
  filters: {},
};

function queryString(r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

export function useOrdini(ricerca: Ricerca) {
  return useQuery({
    queryKey: ['ordini', ricerca],
    queryFn: () => chiama<Pagina>(`/api/orders?${queryString(ricerca)}`),
    placeholderData: (precedente) => precedente,
  });
}

/** Quanti ordini in ciascuno stato, con i filtri correnti (tranne lo stato). */
export function useConteggiStato(ricerca: Ricerca) {
  const { stateId: _fuori, ...filtri } = ricerca.filters ?? {};
  const senzaStato = { ...ricerca, filters: filtri };
  return useQuery({
    queryKey: ['ordini-conteggi', senzaStato],
    queryFn: () =>
      chiama<{
        counts: {
          stateId: number;
          state: string;
          stateOriginal: string;
          colorHex: string | null;
          n: number;
          /** Uno stato che non si usa più (nella Inc il 3, il 4 e il 6). */
          resting?: boolean;
        }[];
      }>(
        `/api/orders/counts?${queryString(senzaStato)}`,
      ),
    placeholderData: (precedente) => precedente,
  });
}

/** Correggere la testata: si manda solo quello che cambia. */
export function useCorreggiTestata(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Ordine>(`/api/orders/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (aggiornato) => {
      client.setQueryData(['ordine', id], aggiornato);
      void client.invalidateQueries({ queryKey: ['ordini'] });
      void client.invalidateQueries({ queryKey: ['transizioni', id] });
    },
  });
}

export function useOrdine(id: number) {
  return useQuery({
    queryKey: ['ordine', id],
    queryFn: () => chiama<Ordine>(`/api/orders/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useDefinizioneFiltriOrdini() {
  return useQuery({
    queryKey: ['filtri-ordini'],
    queryFn: () =>
      chiama<{
        filters: {
          key: string;
          label: string;
          type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
          options?: string;
          values?: { value: string; label: string }[];
        }[];
        options: Record<string, { id: string | number; label: string }[]>;
        /** L'agente non elimina ordini: lo decide il servizio. */
        canDelete: boolean;
      }>('/api/orders/filters'),
    staleTime: 10 * 60_000,
  });
}

/**
 * Scarica gli ordini dell'elenco, con i filtri di adesso. Lingua, colonne e
 * articoli il servizio li legge dalle scelte dell'utente: prima si salvano
 * (`useSalvaEsportazione`), poi si scarica.
 */
export function esportaOrdini(ricerca: Ricerca, formato: 'xlsx' | 'csv'): void {
  window.location.href = `/api/orders/export?${queryString(ricerca, { format: formato })}`;
}

/** Una colonna che si può esportare: dell'ordine o dell'articolo. */
export interface ColonnaEsportazione {
  key: string;
  group: 'order' | 'line';
  label: string;
}

/** Le risposte alla finestra «Esporta», ricordate sull'utente. */
export interface SceltaEsportazione {
  language: 'it' | 'en';
  columns: string[];
  lines: boolean;
}

export function useOpzioniEsportazione() {
  return useQuery({
    queryKey: ['ordini', 'esportazione'],
    queryFn: () =>
      chiama<{ columns: ColonnaEsportazione[]; choice: SceltaEsportazione }>('/api/orders/export/options'),
  });
}

export function useSalvaEsportazione() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (scelta: SceltaEsportazione) =>
      chiama<{ choice: SceltaEsportazione }>('/api/orders/export/options', { metodo: 'PUT', corpo: scelta }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ordini', 'esportazione'] }),
  });
}

export interface EsitoDuplicazione {
  created: { id: number; orderNumber: string; protocolNumber: number }[];
}

/**
 * Duplica un ordine.
 *
 * I numeri delle copie o si contano avanti da quello dell'ordine, o si
 * dettano uno per uno.
 */
export function useDuplicaOrdine(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: {
      copies: number;
      withLines: boolean;
      numbering:
        | { mode: 'progressive'; prefix?: string | null }
        | { mode: 'list'; numbers: string[] };
    }) =>
      chiama<EsitoDuplicazione>(`/api/orders/${id}/duplicate`, { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ordini'] });
      void client.invalidateQueries({ queryKey: ['conteggi-stato'] });
    },
  });
}

/** Com'è andato un invio a Business Central. */
export interface EsitoInvioBc {
  sent: boolean;
  ok: boolean;
  message: string;
  calls?: number;
  accepted?: number;
  refused?: number;
  failed?: number;
  batch?: string;
  doc?: string;
}

/** Una chiamata a BC, come sta nel registro. */
export interface ChiamataBc {
  id: number;
  batch: string;
  trigger: string;
  operation: 'insert' | 'update';
  mode: 'test' | 'live';
  bcCompany: number;
  pass: string;
  method: string;
  doc: string;
  params: Record<string, unknown>;
  returnValue: string | null;
  outcome: 'ok' | 'refused' | 'failed' | 'skipped';
  error: string | null;
  createdAt: string;
}

/** Il registro degli invii a BC dell'ordine, e se l'invio è acceso. */
export function useInviiBc(id: number, attivo: boolean) {
  return useQuery({
    queryKey: ['invii-bc', id],
    queryFn: () =>
      chiama<{ mode: 'spento' | 'prova' | 'acceso'; sends: ChiamataBc[] }>(
        `/api/orders/${id}/bc-sends`,
      ),
    enabled: attivo,
  });
}

/**
 * «Invia a BC»: inserimento o aggiornamento, a mano.
 *
 * Rifare l'inserimento di un ordine che BC ha già chiede conferma: il
 * servizio risponde `serveConferma`, e si riprova con `confirm`.
 */
export function useInviaABc(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { operation: 'insert' | 'update'; confirm?: boolean }) =>
      chiama<EsitoInvioBc>(`/api/orders/${id}/send-to-bc`, { metodo: 'POST', corpo }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['invii-bc', id] });
    },
  });
}

/**
 * Cosa risponde l'invio al vettore.
 *
 * I due vettori rispondono in modo diverso perché funzionano in modo diverso:
 * C&L rimanda indietro il tracking — e allora `righe` dice quante righe ha
 * mandato — mentre Interglobo prende l'XML e basta, e l'unica cosa che si
 * può dire è il nome del file posato.
 */
export interface EsitoInvioVettore {
  esito: 'applicato' | 'mandato' | 'ordineNonTrovato' | 'ordineDoppio' | 'nienteDaFare';
  cambiati: string[];
  righe: number;
  notified: number;
  fileName?: string;
  /** C'è solo quando non è andato tutto: non è uscito niente, o non tutto. */
  message?: string;
}

/**
 * Manda l'ordine al vettore.
 *
 * Per C&L la stessa chiamata porta indietro il tracking, quindi appena
 * risponde la scheda ha già le navi e le date nuove: per questo si ricarica
 * l'ordine invece di limitarsi a dire «fatto».
 */
export function useInviaAlVettore(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (forza?: boolean) =>
      chiama<EsitoInvioVettore>(`/api/orders/${id}/send-to-carrier`, {
        metodo: 'POST',
        corpo: forza ? { force: true } : {},
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ordine', id] });
      void client.invalidateQueries({ queryKey: ['avvisi'] });
    },
  });
}
